import { Button, Divider, Modal, Table } from 'antd';
import React, { useEffect, useState } from 'react';
import { RootState } from '../../../rootReducer';
import { AppDispatch } from '../../../store';
import { SelectOption } from '../Annotation/annotation.types';
import {
  createFailureMechanismTemplate,
  deleteFailureMechanismTemplate,
  FailureMechanismTemplate,
  getFailureMechanismTemplates,
  setFormResult,
  setSelectedCode,
  setSelectedName,
  updateFailureMechanismTemplate
} from './failureMechanismTemplate.slice';
import styles from './FailureMechanismTemplate.module.scss';
import SearchItem from '../../Shared/Landing/SearchForm/SearchItem';
import FailureMechanismTemplateForm from './FailureMechanismTemplateForm';
import { connect } from 'react-redux';
import { deleteOrDeprecate } from '../../../shared/Helpers';

type Props = {
  failureMechanismTemplates: FailureMechanismTemplate[];
  loading: boolean;
  formLoading: boolean;
  size: number;
  currentPage: number;
  totalNumber: number;
  names: SelectOption[];
  selectedName?: string;
  codes: SelectOption[];
  selectedCode?: string;
  formResult?: boolean;
  onFailureMechanismTemplateInit: (take: number, newPage: number) => void;
  onSetSelectedName: (name: string | undefined) => void;
  onSetSelectedCode: (code: string | undefined) => void;
  onCreate: (template: FailureMechanismTemplate) => void;
  onUpdate: (template: FailureMechanismTemplate) => void;
  onDelete: (id: string) => void;
  onSetFormResult: (result: boolean | undefined) => void;
};

const FailureMechanismTemplateTable = ({
  failureMechanismTemplates,
  loading,
  formLoading,
  size,
  currentPage,
  totalNumber,
  names,
  selectedName,
  codes,
  selectedCode,
  formResult,
  onFailureMechanismTemplateInit,
  onSetSelectedName,
  onSetSelectedCode,
  onCreate,
  onUpdate,
  onDelete,
  onSetFormResult
}: Props) => {
  const [showForm, setShowForm] = useState(false);
  const [selectedFailureMechanismTemplate, setSelectedFailureMechanismTemplate] = useState<FailureMechanismTemplate>();

  useEffect(() => {
    onFailureMechanismTemplateInit(size, 1);
  }, [onFailureMechanismTemplateInit, size]);

  useEffect(() => {
    if (formResult !== false) {
      onHideModal();
      onSetFormResult(undefined);
    }
  }, [formResult, onSetFormResult]);

  const onAdd = () => {
    setShowForm(true);
  };

  const onEdit = (record: FailureMechanismTemplate) => {
    setSelectedFailureMechanismTemplate(record);
    setShowForm(true);
  };

  const onHideModal = () => {
    setSelectedFailureMechanismTemplate(undefined);
    setShowForm(false);
  };

  const onRowDelete = (id: any, method: string) => {
    Modal.confirm({
      title: 'Confirm',
      content: `Are you sure you want to ${method} Failure Mechanism template?`,
      okText: 'Yes',
      cancelText: 'No',
      onOk: async () => {
        onDelete(id);
      }
    });
  };

  const onSubmit = (model: FailureMechanismTemplate) => {
    if (selectedFailureMechanismTemplate) {
      onUpdate(model);
    } else {
      onCreate(model);
    }
  };

  const columns = [
    {
      title: 'Code',
      key: 'code',
      dataIndex: 'code'
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description'
    },
    {
      title: 'Action',
      key: 'action',
      render: (record: any) => (
        <span>
          {!record.deprecated && (
            <>
              <Button type='link' disabled={record.deprecated} onClick={() => onEdit(record)}>
                Update
              </Button>
              <Divider type='vertical' />
              <Button type='link' onClick={() => onRowDelete(record.id, deleteOrDeprecate(record.canDelete))}>
                {deleteOrDeprecate(record.canDelete)}
              </Button>
            </>
          )}
          {record.deprecated && (
            <Button type='link' onClick={() => {}} disabled={true}>
              Deprecated
            </Button>
          )}
        </span>
      )
    }
  ];
  return (
    <div className={styles.container}>
      <h1 className='mainheader'>Failure Mechanism Templates</h1>
      {showForm ? (
        <FailureMechanismTemplateForm
          selectedFailureMechanismTemplate={selectedFailureMechanismTemplate}
          loading={formLoading}
          onHideModal={onHideModal}
          onSubmit={onSubmit}
        />
      ) : (
        ''
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span style={{ marginRight: '1rem' }}>
            <SearchItem
              label='Code'
              value={selectedCode}
              values={codes}
              onChange={value => {
                onSetSelectedCode(value);
                onFailureMechanismTemplateInit(size, 1);
              }}
            />
          </span>
          <SearchItem
            label='Name'
            value={selectedName}
            values={names}
            onChange={value => {
              onSetSelectedName(value);
              onFailureMechanismTemplateInit(size, 1);
            }}
          />
        </div>
        <Button className={styles.addFailureMechanism} onClick={() => onAdd()}>
          Add Failure Mechanism
        </Button>
      </div>
      <Table
        loading={loading}
        columns={columns}
        dataSource={failureMechanismTemplates}
        rowKey='id'
        scroll={{ y: 500 }}
        pagination={{
          current: currentPage,
          total: totalNumber,
          pageSize: size,
          onChange: (page: number) => {
            onFailureMechanismTemplateInit(size, page);
          },
          onShowSizeChange: (current: number, size: number) => {
            onFailureMechanismTemplateInit(size, 1);
          }
        }}
      />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  failureMechanismTemplates: state.failureMechanismTemplate.failureMechanismTemplates,
  loading: state.failureMechanismTemplate.loading,
  formLoading: state.failureMechanismTemplate.formLoading,
  size: state.failureMechanismTemplate.size,
  currentPage: state.failureMechanismTemplate.currentPage,
  totalNumber: state.failureMechanismTemplate.totalNumber,
  names: state.failureMechanismTemplate.names,
  selectedName: state.failureMechanismTemplate.selectedName,
  codes: state.failureMechanismTemplate.codes,
  selectedCode: state.failureMechanismTemplate.selectedCode,
  formResult: state.failureMechanismTemplate.formResult
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  onFailureMechanismTemplateInit: (take: number, newPage: number) => dispatch(getFailureMechanismTemplates(take, newPage)),
  onSetSelectedName: (name: string | undefined) => dispatch(setSelectedName(name)),
  onSetSelectedCode: (code: string | undefined) => dispatch(setSelectedCode(code)),
  onCreate: (template: FailureMechanismTemplate) => dispatch(createFailureMechanismTemplate(template)),
  onUpdate: (template: FailureMechanismTemplate) => dispatch(updateFailureMechanismTemplate(template)),
  onDelete: (id: string) => dispatch(deleteFailureMechanismTemplate(id)),
  onSetFormResult: (result: boolean | undefined) => dispatch(setFormResult(result))
});

export default connect(mapStateToProps, mapDispatchToProps)(FailureMechanismTemplateTable);
